<script setup lang="ts">
import Dropdown, { type DropdownChangeEvent } from "primevue/dropdown";
import { computed } from "vue";
import { useToast } from "vue-toast-notification";
import CheckboxCheckmark from "@/components/shared/icons/CheckboxCheckmark.vue";
import { ERROR_TIMEOUT } from "@/constants.js";
import { useUpdateTodoStatus } from "@/providers/todosSubmissions/useUpdateTodoStatus";
import { todoStatusDropdownPt } from "./styles";
import type { TodoStatus } from "./types";
import { LOCKED_STATUS, SELECTABLE_STATUS_ORDER, STATUS_DISPLAY_MAP, STATUS_DOT_CLASSES } from "./types";

interface TodoStatusDropdownProps {
  participantId: string;
  applicationId: string;
  todoId: string;
  status: TodoStatus;
  permittedStatuses: TodoStatus[];
}

interface StatusOption {
  status: TodoStatus;
  disabled: boolean;
}

const props = defineProps<TodoStatusDropdownProps>();

const toast = useToast();
const { updateStatus } = useUpdateTodoStatus();

const options = computed<StatusOption[]>(() => {
  const selectable = SELECTABLE_STATUS_ORDER.filter((status) => props.permittedStatuses.includes(status)).map(
    (status) => ({ status, disabled: false }),
  );
  if (props.permittedStatuses.includes(LOCKED_STATUS)) {
    selectable.push({ status: LOCKED_STATUS, disabled: true });
  }
  return selectable;
});

const dropdownPt = computed(() => todoStatusDropdownPt(props.status));

const displayLabel = (status: TodoStatus): string => STATUS_DISPLAY_MAP[status] ?? status;

const isSelected = (status: TodoStatus): boolean => status === props.status;

const onChange = async (event: DropdownChangeEvent): Promise<void> => {
  if (!event.value || event.value === props.status || event.value === LOCKED_STATUS) return;
  try {
    await updateStatus.mutateAsync({
      participantId: props.participantId,
      applicationId: props.applicationId,
      todoId: props.todoId,
      status: event.value,
    });
  } catch {
    toast.open({
      message: "Error while updating the to-do status. Please try again.",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  }
};
</script>

<template>
  <Dropdown
    :model-value="status"
    :options="options"
    option-value="status"
    option-disabled="disabled"
    :disabled="updateStatus.isPending.value"
    :focus-on-hover="false"
    :pt="dropdownPt"
    data-testid="todo-status-dropdown"
    @change="onChange"
  >
    <template #header>
      <div class="px-2.5 pt-1.5 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-500">Set status</div>
    </template>
    <template #value="{ value }">
      <span>{{ displayLabel(value) }}</span>
    </template>
    <template #option="{ option }">
      <span v-if="option.disabled" class="flex items-center gap-2">
        <span class="w-2.5 h-2.5 rounded-full shrink-0 bg-gray-500" />
        <span class="flex-1">
          <span class="block text-sm font-semibold text-gray-600">{{ displayLabel(option.status) }}</span>
          <span class="block text-xs text-gray-500 leading-snug mt-0.5">
            Requires feedback — click into this to-do to update
          </span>
        </span>
      </span>
      <span v-else class="flex items-center gap-2 w-full">
        <span class="w-2.5 h-2.5 rounded-full shrink-0" :class="STATUS_DOT_CLASSES[option.status]" />
        <span class="text-sm font-semibold text-indigo-base flex-1">{{ displayLabel(option.status) }}</span>
        <CheckboxCheckmark
          v-if="isSelected(option.status)"
          class="w-4 h-4 shrink-0"
          rgb-fill-color="#007F80"
        />
      </span>
    </template>
  </Dropdown>
</template>
